import React, { useEffect, useState } from 'react';
import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import { Navbar, Container, Nav, Image, Row, Col, NavDropdown, Offcanvas, Button } from 'react-bootstrap';
import { logout, getCurrentUser } from '../services/authService'; // Import logout and user getter
import ProfileModal from './ProfileModal'; // Import ProfileModal
import CaseManagerAssignmentGuide from './CaseManagerAssignmentGuide/CaseManagerAssignmentGuide';
import { usePermissions } from '../Hooks/usePermission';
import { PERMISSIONS } from '../lib/utils/permissions';
import { BriefcaseFill, BuildingFill, PeopleFill } from 'react-bootstrap-icons';
import {
  BoxArrowRightIcon,
  DotIcon,
  FileTextIcon,
  HouseIcon,
  ListIcon,
  PersonBadgeIcon,
  PersonCircleIcon,
  ProfileIcon,
  QuestionCircleIcon,
  SmartwatchIcon,
} from "./svg";


function DashboardLayout() {
  const location = useLocation();
  // --- RETRIEVE USER DATA ---
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCaseManagerGuide, setShowCaseManagerGuide] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    reports: location.pathname.startsWith('/reports'),
  });
  // --- UPDATED: Manage User State ---
  const [user, setUser] = useState(getCurrentUser() || {});
  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const canCreatePayment = can(PERMISSIONS.PAYMENT_MANAGE_CLIENT, userRoleCode);
  const canViewEntity = can(PERMISSIONS.ENTITY_READ, userRoleCode);
  const canViewClient = can(PERMISSIONS.CLIENT_READ, userRoleCode);
  const canViewAdminUsers = can(PERMISSIONS.USER_READ, userRoleCode);
  const canViewDeviceUsers = can(PERMISSIONS.CUSTOMER_READ, userRoleCode);
  const canViewDevices = can(PERMISSIONS.DEVICE_READ_INVENTORY, userRoleCode);
  const canViewReports = can(PERMISSIONS.REPORT_CUSTOMER_ENROLMENT_READ, userRoleCode);

  const firstName = user.firstName || 'Admin';
  const lastName = user.lastName || '';
  const roleName = user.role?.name || 'User'; // Access nested role object

  // --- NEW: Handle Profile Update from Modal ---
  const handleProfileUpdate = (updatedUser) => {
    // 1. Update State to refresh UI
    setUser(updatedUser);
    // 2. Persist to LocalStorage so refresh keeps changes
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Handle Logout click
  const handleLogout = (e) => {
    e.preventDefault();
    logout();
  };

  const sidebarItems = [
    { to: '/', label: 'Dashboard', icon: <HouseIcon /> },
    { to: '/entity', label: 'Entities', icon: <BuildingFill />, canView: canViewEntity },
    { to: '/client', label: 'Clients', icon: <BriefcaseFill />, canView: canViewClient },
    { to: '/users', label: 'Admin Users', icon: <PeopleFill />, canView: canViewAdminUsers },
    { to: '/customers', label: 'Device Users', icon: <PersonBadgeIcon size={16} />, canView: canViewDeviceUsers },
    { to: '/devices', label: 'Devices', icon: <SmartwatchIcon size={16} />, canView: canViewDevices },
    {
      to: '/reports',
      label: 'Reports',
      icon: <FileTextIcon />,
      canView: canViewReports,
      children: [
        {
          to: '/reports?code=customer_enrolment',
          label: 'Enrollment Status Report',
        },
      ],
    },
  ];
  const isReportsActive = location.pathname.startsWith('/reports');
  const toggleSubmenu = (key) => {
    setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  useEffect(() => {
    if (!location.pathname.startsWith('/reports')) {
      setExpandedMenus((prev) => ({ ...prev, reports: false }));
    }
  }, [location.pathname]);

  return (
    <div className="d-flex flex-column vh-100" style={{ backgroundColor: '#f8f9fa' }}>

      <Navbar className="bg-allwell-gradient" variant="dark" expand="lg">
        <Container fluid className="px-4">
          <Button
            variant="link"
            className="allwell-menu-trigger p-0 me-3 d-flex align-items-center justify-content-center"
            onClick={() => setShowSidebar(true)}
            aria-label="Open sidebar menu"
          >
            <ListIcon className="allwell-menu-trigger__icon" />
          </Button>

          <Navbar.Brand as={Link} to="/" className="fw-bold fs-4 d-flex align-items-center">
            <Image
              src="/images/logo.png"
              alt="AllWell Logo"
              style={{ height: '60px', width: "200px", marginRight: '10px' }}
            />
            {/* AllWell Admin Portal */}
          </Navbar.Brand>

          <Nav className="ms-auto d-flex flex-row align-items-center">

            {/* === FIX 2: Applied white classes DIRECTLY to the icons === */}
            <Nav.Link as={Link} to="/" className="me-2">
              <HouseIcon className="text-white-50" />
            </Nav.Link>
            {/* <Nav.Link href="#settings" className="me-3">
              <GearIcon className="text-white-50" />
            </Nav.Link> */}

            {/* --- NEW: Help Dropdown --- */}
            <NavDropdown
              title={<QuestionCircleIcon className="text-white" />}
              id="help-dropdown"
              className="user-dropdown-no-arrow me-3 mt-1"
              align="end"
            >
              <NavDropdown.Item
                href="/AllWell_Customer_Quick_Start_Guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                AllWell Customer Quick Start Guide
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => setShowCaseManagerGuide(true)}
              >
               Case Manager Reassignment Guide
              </NavDropdown.Item>
            </NavDropdown>

            {/* --- User Dropdown --- */}
            <NavDropdown
              align="end"
              title={
                <div className="d-flex align-items-center">
                  <PersonCircleIcon className="text-white" />
                  <div className="d-none d-md-block ms-2 text-start">
                    <div className="fw-bold text-white lh-1">{firstName} {lastName}</div>
                    <div className="text-white-50" style={{ fontSize: '0.75rem' }}>{roleName}</div>
                  </div>
                </div>
              }
              id="user-nav-dropdown"
              className="user-dropdown-no-arrow" // You might want to hide the arrow with CSS
            >
              <>
                <NavDropdown.Item onClick={() => setShowProfileModal(true)} className="d-flex align-items-center gap-2">
                  <ProfileIcon className="text-secondary" /> My Profile
                </NavDropdown.Item>
                <NavDropdown.Divider />
              </>
              <NavDropdown.Item onClick={handleLogout} className="d-flex align-items-center gap-2 text-danger">
                <BoxArrowRightIcon /> Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </Navbar>

      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="start"
        className="allwell-sidebar"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>
            <Link to="/" onClick={() => setShowSidebar(false)} className="d-inline-flex align-items-center">
              <Image
                src="/images/logo.png"
                alt="AllWell Logo"
                style={{ height: '44px', width: '150px', objectFit: 'contain' }}
              />
            </Link>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <Nav className="flex-column py-2">
            {sidebarItems
              .filter((item) => item.to === '/' || item.canView)
              .map((item) => (
              <div key={item.to}>
                {item.children ? (
                  <button
                    type="button"
                    onClick={() => toggleSubmenu('reports')}
                    className={`allwell-sidebar-link allwell-sidebar-toggle d-flex align-items-center w-100 border-0 ${isReportsActive ? 'active' : ''}`}
                  >
                    <span className="d-inline-flex align-items-center gap-2">
                      {item.icon}
                      {item.label}
                    </span>
                    <span className={`allwell-sidebar-toggle__arrow ${expandedMenus.reports ? 'expanded' : ''}`}>▾</span>
                  </button>
                ) : (
                  <NavLink
                    to={item.to}
                    onClick={() => setShowSidebar(false)}
                    end={item.to === '/'}
                    className={({ isActive }) => `allwell-sidebar-link d-flex align-items-center ${isActive ? 'active' : ''}`}
                  >
                    <span className="d-inline-flex align-items-center gap-2">
                      {item.icon}
                      {item.label}
                    </span>
                  </NavLink>
                )}
                {item.children && expandedMenus.reports && (
                  <div className="allwell-sidebar-submenu">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        onClick={() => setShowSidebar(false)}
                        className={({ isActive }) => `allwell-sidebar-sublink d-flex align-items-center ${isActive ? 'active' : ''}`}
                      >
                        <DotIcon className="allwell-sidebar-sublink__icon me-2" />
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* 2. Main Content Area */}
      <Container fluid className="px-4 py-4 flex-grow-1">
        <Outlet />
      </Container>

      {/* 3. Footer */}
      <footer className="bg-white py-3 mt-auto shadow-sm">
        <Container fluid className="px-4">
          <Row>
            <Col className="text-muted" style={{ fontSize: '0.9rem' }}>
              &copy; {new Date().getFullYear()} Smart Monitor Corp - All Rights Reserved.
            </Col>
            {/* <Col className="text-end text-muted" style={{ fontSize: '0.9rem' }}>
              Powered By <strong>Smart Monitor</strong>
            </Col> */}
          </Row>
        </Container>
      </footer>

      {/* --- Global Profile Modal --- */}
      <ProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        user={user}
        onProfileUpdate={handleProfileUpdate} // <-- Pass this function
        canCreatePayment={canCreatePayment}
      />

      <CaseManagerAssignmentGuide
        show={showCaseManagerGuide}
        onHide={() => setShowCaseManagerGuide(false)}
      />
    </div>
  );
}

export default DashboardLayout;